"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Clock, UserCheck, ShieldCheck, ArrowRight, Sparkles, Layers, MessageSquare } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const affordablePoints = [{
  title: "Dedicated consultant access",
  desc: "You work one-to-one with me — no junior pass-offs, account coordinators, or automated ticket queues."
}, {
  title: "Prioritised high-impact scope",
  desc: "We focus budget strictly on highest-leverage search fixes and revenue opportunities rather than busywork."
}, {
  title: "Strategic depth & custom plans",
  desc: "Audits and campaigns built around your commercial goals, technical stack, and competitive search landscape."
}, {
  title: "Transparent investment tiers",
  desc: "Clearly scoped retainers (£300–£500/month for focused tasks or £1,000–£2,500/month for comprehensive growth)."
}];
const lowCostPoints = [{
  title: "Standardised task execution",
  desc: "Fixed checklist routines applied uniformly across multiple accounts with limited room for bespoke strategy."
}, {
  title: "Strictly capped time allocation",
  desc: "Sub-£100 or starter packages (£99/month) naturally provide fewer dedicated hours each month."
}, {
  title: "Automated deliverables",
  desc: "Reports and surface-level audits rely largely on automated software exports with minimal human synthesis."
}, {
  title: "Shared support channels",
  desc: "Communication is usually handled via generic ticketing systems or pooled account management."
}];
const comparisonRows = [{
  facet: "Scope & Focus",
  affordable: "Customised roadmaps targeting your specific bottlenecks and commercial intent keywords.",
  cheap: "Fixed monthly checklists and generic task lists across varied verticals."
}, {
  facet: "Resource Allocation",
  affordable: "Substantial direct consultant hours dedicated to deep strategy and hands-on fixes.",
  cheap: "Very limited time per account to maintain low service subscription fees."
}, {
  facet: "Strategy Depth",
  affordable: "Holistic technical, on-page, and AI search visibility built around commercial return.",
  cheap: "Primarily automated site crawls and baseline meta tag adjustments."
}, {
  facet: "Communication",
  affordable: "One-on-one calls, clear bespoke video walk-throughs, and prompt WhatsApp/email replies.",
  cheap: "Automated monthly PDF reports and shared support ticketing inboxes."
}];
export default function AffordableComparison() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve = [0.16, 1, 0.3, 1] as const;
  const headerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 24
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: easeCurve
      }
    }
  };
  const cardContainerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardItemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: easeCurve
      }
    }
  };
  return <section id="affordablevscheapseo" data-nav="light" className="bg-background py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header Group */}
        <motion.div variants={headerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.3
      }} className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2">
            <Badge variant="secondary" className="rounded-full bg-secondary/80 px-3.5 py-1 text-xs font-semibold text-accent-foreground border-border/40">
              <Sparkles className="h-3.5 w-3.5 text-accent" />
              Transparent Comparison
            </Badge>
          </div>

          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Affordable SEO vs Cheap SEO: What&apos;s the Real Difference?
          </h2>

          <p className="mt-5 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
            When you evaluate search services, price reflects scope and resource allocation. As an independent consultant, I define affordable SEO as professional, high-leverage guidance tailored to what your business realistically needs — without paying for agency overhead or settling for cookie-cutter checklist routines.
          </p>
        </motion.div>

        {/* Two-Column Comparison Cards */}
        <motion.div variants={cardContainerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Card 1: Affordable, Professional SEO */}
          <motion.div variants={cardItemVariants}>
            <Card className="h-full border-2 border-primary/30 bg-card shadow-sm transition-[border-color,box-shadow] hover:border-primary/60 hover:shadow-md">
              <CardHeader className="border-b border-border/60 pb-6">
                <div className="flex items-center justify-between">
                  <span className="rounded-md bg-primary/10 px-2.5 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-primary">
                    Tailored & Personal
                  </span>
                  <span className="font-sans text-xs text-muted-foreground">
                    £300–£500 / £1k–£2.5k / mo
                  </span>
                </div>
                <CardTitle className="mt-3 font-serif text-2xl font-bold text-foreground">
                  Affordable, Professional SEO
                </CardTitle>
                <p className="font-sans text-sm text-muted-foreground">
                  High-leverage prioritisation driven directly by an experienced specialist.
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-4">
                  {affordablePoints.map((item, i) => <li key={item.title} data-index={i} className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-full bg-primary/10 p-1 text-primary">
                        <CheckCircle2 className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="font-sans text-sm font-semibold text-foreground">
                          {item.title}
                        </h3>
                        <p className="mt-0.5 font-sans text-xs leading-relaxed text-muted-foreground">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 2: Standardised Low-Cost SEO Services */}
          <motion.div variants={cardItemVariants}>
            <Card className="h-full border border-border bg-card/60 shadow-xs transition-[border-color,box-shadow] hover:border-border/80">
              <CardHeader className="border-b border-border/60 pb-6">
                <div className="flex items-center justify-between">
                  <span className="rounded-md bg-muted px-2.5 py-1 font-mono text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Standardised Delivery
                  </span>
                  <span className="font-sans text-xs text-muted-foreground">
                    e.g. £99/mo starter tiers
                  </span>
                </div>
                <CardTitle className="mt-3 font-serif text-2xl font-bold text-foreground">
                  Low-Cost (&lsquo;Cheap&rsquo;) SEO Services
                </CardTitle>
                <p className="font-sans text-sm text-muted-foreground">
                  Economical entry packages scaled through templates and strict time caps.
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-4">
                  {lowCostPoints.map((item, i) => <li key={item.title} data-index={i} className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-full bg-muted p-1 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="font-sans text-sm font-semibold text-foreground">
                          {item.title}
                        </h3>
                        <p className="mt-0.5 font-sans text-xs leading-relaxed text-muted-foreground">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Structured Comparison Table */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: easeCurve,
        delay: 0.1
      }} className="mt-14 overflow-hidden rounded-xl border border-border bg-card shadow-xs">
          <div className="border-b border-border bg-muted/30 px-6 py-4">
            <h3 className="font-serif text-lg font-semibold text-foreground">
              Key Facets at a Glance
            </h3>
            <p className="font-sans text-xs text-muted-foreground">
              A clear comparison between bespoke consulting and volume-based packages
            </p>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border bg-muted/10">
                  <TableHead className="w-1/4 py-3 font-serif text-xs font-semibold uppercase tracking-wider text-foreground">
                    Facet
                  </TableHead>
                  <TableHead className="w-3/8 py-3 font-serif text-xs font-semibold uppercase tracking-wider text-primary">
                    Affordable Professional SEO (Direct)
                  </TableHead>
                  <TableHead className="w-3/8 py-3 font-serif text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Standard Low-Cost Packages
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonRows.map((row, i) => <TableRow key={row.facet} data-index={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <TableCell className="align-top py-4 font-sans font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        {i === 0 && <Layers className="h-4 w-4 text-primary shrink-0" />}
                        {i === 1 && <Clock className="h-4 w-4 text-primary shrink-0" />}
                        {i === 2 && <ShieldCheck className="h-4 w-4 text-primary shrink-0" />}
                        {i === 3 && <MessageSquare className="h-4 w-4 text-primary shrink-0" />}
                        <span>{row.facet}</span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top py-4 font-sans text-sm text-foreground/90 whitespace-normal leading-relaxed">
                      {row.affordable}
                    </TableCell>
                    <TableCell className="align-top py-4 font-sans text-sm text-muted-foreground whitespace-normal leading-relaxed">
                      {row.cheap}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </motion.div>

        {/* CTA Bar */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 0.5,
        ease: easeCurve,
        delay: 0.15
      }} className="mt-12 rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="text-center sm:text-left">
            <h3 className="font-serif text-lg font-bold text-foreground sm:text-xl">
              Unsure which search scope fits your goals?
            </h3>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              Let&apos;s review your website together and find the right balance of investment and growth.
            </p>
          </div>

          <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground font-sans font-medium shadow-sm hover:bg-primary/90 transition-colors shrink-0">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              <span>Book a Free SEO Consultation</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}